import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import queryClient from "@/query-client";
import { DEFAULT_STALE_TIME } from "@/constants";
import { markAuditInitiated } from "@/resources/listing/listing.initiator";
import type {
  CreateScorecardParams,
  CreateScorecardResponse,
  ScorecardListItem,
  ScorecardResponse,
} from "@/types/scorecard";

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 10 * 60 * 1000;

const pollStartMap = new Map<number, number>();

export const useCreateScorecard = () =>
  useMutation<CreateScorecardResponse, unknown, CreateScorecardParams>({
    mutationFn: (params) =>
      apiService.post<CreateScorecardResponse, { asin: string; country?: string; brand_id?: number }>(
        "/scorecards",
        {
          asin: params.asin,
          country: params.country,
          brand_id: params.brandId,
        }
      ),
    onSuccess: (data) => {
      // Marks this tab as the one that ran the audit, keyed by ASIN so
      // `/spi` (no product id) and the product-page audit screen agree
      // (`listing.initiator.ts`, `decisions/a-listing-toast-is-addressed-by-user-and-tab-mark.md`).
      markAuditInitiated(data.asin);
      queryClient.invalidateQueries({ queryKey: ["scorecards", "list", data.asin] });
    },
  });

export const useGetScorecard = (id?: number) =>
  useQuery<ScorecardResponse>({
    queryKey: ["scorecards", "show", id],
    enabled: !!id,
    queryFn: async () => {
      const scorecardId = id as number;

      if (!pollStartMap.has(scorecardId)) {
        pollStartMap.set(scorecardId, Date.now());
      }

      const startTime = pollStartMap.get(scorecardId) as number;
      if (Date.now() - startTime > MAX_POLL_DURATION_MS) {
        pollStartMap.delete(scorecardId);
        throw new Error("Amazon AI Score generation timed out after 6 minutes.");
      }

      return apiService.get<ScorecardResponse>(`/scorecards/${scorecardId}`);
    },
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;
      const data = query.state.data;
      if (!data) return POLL_INTERVAL_MS;
      const isActive = data.status === "pending" || data.status === "processing";
      if (!isActive) {
        pollStartMap.delete(id as number);
      }
      return isActive ? POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: true,
  });

export const useListScorecards = (asin?: string, brandId?: number, limit = 10) =>
  useQuery<ScorecardListItem[]>({
    queryKey: ["scorecards", "list", asin, brandId, limit],
    enabled: !!asin,
    queryFn: () =>
      apiService.get<ScorecardListItem[], { asin: string | undefined; limit: number; brand_id: number | undefined }>(
        "/scorecards",
        { asin, limit, brand_id: brandId }
      ),
    staleTime: DEFAULT_STALE_TIME,
  });
